import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  BlockStack,
  Text,
} from '@shopify/polaris';
import { useAuth } from './AuthProvider';
import { ApiError } from '@/types/api';

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authenticated') {
    const target = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
    navigate(target, { replace: true });
  }

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const target = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'unauthorized') setError('Invalid email or password.');
        else if (err.code === 'rate_limited') setError('Too many attempts. Try again later.');
        else setError(err.message);
      } else {
        setError('Unexpected error. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Page narrowWidth>
      <BlockStack gap="600">
        <BlockStack gap="200" align="center">
          <Text as="h1" variant="heading2xl">
            Padel Needs Admin
          </Text>
          <Text as="p" tone="subdued">
            Sign in to manage your theme
          </Text>
        </BlockStack>
        <Card>
          <form onSubmit={handleSubmit}>
            <FormLayout>
              {error && <Banner tone="critical">{error}</Banner>}
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                autoFocus
                disabled={submitting}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                disabled={submitting}
              />
              <Button submit variant="primary" fullWidth loading={submitting}>
                Sign in
              </Button>
            </FormLayout>
          </form>
        </Card>
      </BlockStack>
    </Page>
  );
}
